import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { SearchIcon } from '@/icons';
import { CsvIcon } from '@/icons/csv-icon';
import { PlusIcon } from '@/icons/plus-icon';
import { cn } from '@/lib/utils';
import { CLUB_VERIFICATION_STATUS } from '@/services/dashboard/reservationLedger/reservationLedger.types';
import { PLAN_REQUEST_STATUS } from '@/services/hoslog-plans/hoslog-plans-request.types';
import { STORE_STATUS } from '@/services/premiumStores/premiumStores.types';
import type { Column, Table } from '@tanstack/react-table';
import { X } from 'lucide-react';
import * as React from 'react';
import { Button } from 'ui/button';
import { Input } from 'ui/input';
import { Label } from 'ui/label';
import type { DateRangePreset } from '../DateRangePicker';
import { DataTableFacetedFilter } from './FacetedFilter';
import { DataTableDateFilter } from './Filter';
import { DataTablePagination } from './Pagination';
import { DataTableSliderFilter } from './SliderFilter';
import Select from './TableSelect';
import { DataTableViewOptions } from './ViewOptions';
import { sanitizeTableSearch } from './data-table.utils';

type TableAction = {
  key: string;
  label?: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
};

type TableMetaWithTitle = {
  title?: string;
  action?: () => void;
  actionKey?: 'onAddNew' | 'exportCsv';
  actionDisabled?: boolean;
  actions?: TableAction[];
  selectClassName?: string;
  hideSearch?: boolean;
  searchPlaceholder?: string;
  defaultDatePreset?: DateRangePreset;
};

interface DataTableToolbarModalConfig {
  modalTitle: string;
  title: string;
  okText?: string;
  cancelText?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  component: React.ReactNode;
}

interface DataTableToolbarProps<TData> extends React.ComponentProps<'div'> {
  table?: Table<TData> & {
    options: {
      meta?: TableMetaWithTitle;
    };
  };
  children?: React.ReactNode;
  filterChildren?: React.ReactNode;
  hideViewOptions?: boolean;
  dateFilterFirst?: boolean;
  allowFutureDates?: boolean;
  defaultDatePreset?: DateRangePreset;
  defaultDatePresetIsFilter?: boolean;
  customFiltersActive?: boolean;
  onResetCustomFilters?: () => void;
  onOpen?: (config: DataTableToolbarModalConfig) => void;
  modalConfig?: DataTableToolbarModalConfig;
  addButtonLabel?: string;
  buttonText?: string;
  statusFilter?: CLUB_VERIFICATION_STATUS | PLAN_REQUEST_STATUS | STORE_STATUS;
  clubFilter?: string;
  planFilter?: string;
  paymentMethodFilter?: string;
  ratingsFilter?: string;
  locationFilter?: string;
  onStatusFilterChange?: (value: string) => void;
  onClubFilterChange?: (value: string) => void;
  onPlanFilterChange?: (value: string) => void;
  onPaymentMethodFilterChange?: (value: string) => void;
  onRatingsFilterChange?: (value: string) => void;
  onLocationFilterChange?: (value: string) => void;
}

export function DataTableToolbar<TData>({
  table,
  children,
  filterChildren,
  hideViewOptions = false,
  dateFilterFirst = false,
  allowFutureDates = false,
  defaultDatePreset = 'thisMonth',
  defaultDatePresetIsFilter = true,
  customFiltersActive = false,
  onResetCustomFilters,
  className,
  statusFilter,
  clubFilter,
  planFilter,
  paymentMethodFilter,
  ratingsFilter,
  locationFilter,
  onStatusFilterChange,
  onClubFilterChange,
  onPlanFilterChange,
  onPaymentMethodFilterChange,
  onRatingsFilterChange,
  onLocationFilterChange,
  buttonText,
  ...props
}: DataTableToolbarProps<TData>) {
  if (!table) {
    throw new Error('either pass a `table` instance or use it inside DataTable.');
  }

  const title = table.options.meta?.title ?? 'Untitled';
  const action = table.options.meta?.action ?? null;
  const actionDisabled = table.options.meta?.actionDisabled ?? false;
  const activeDatePreset = table.options.meta?.defaultDatePreset ?? defaultDatePreset;
  const showDefaultDateRangeWhenEmpty = activeDatePreset === 'today';
  const columns = React.useMemo(() => table.getAllColumns().filter((column) => column.getCanFilter()), [table]);
  const selectClassName = table.options.meta?.selectClassName ?? '';

  const hideableColumns = React.useMemo(() => table.getAllColumns().filter((column) => column.getCanHide()), [table]);
  const hasHiddenColumns = hideableColumns.some((column) => !column.getIsVisible());
  const isFiltered = Boolean(
    table.getState().columnFilters.length > 0 ||
      statusFilter ||
      clubFilter ||
      planFilter ||
      paymentMethodFilter ||
      ratingsFilter ||
      locationFilter ||
      customFiltersActive ||
      hasHiddenColumns,
  );

  const createdDateColumn = columns.find((column) => column.columnDef.id === 'createdDate');
  const hasDateFilter = Boolean(createdDateColumn);
  const hasViewOptions = !hideViewOptions && hideableColumns.length > 1;

  const actionKey = table.options.meta?.actionKey;

  const onReset = React.useCallback(() => {
    table.resetColumnFilters();
    table.resetColumnVisibility(true);
    if (
      onStatusFilterChange ||
      onClubFilterChange ||
      onPlanFilterChange ||
      onPaymentMethodFilterChange ||
      onRatingsFilterChange ||
      onLocationFilterChange
    ) {
      onStatusFilterChange?.('');
      onClubFilterChange?.('');
      onPlanFilterChange?.('');
      onPaymentMethodFilterChange?.('');
      onRatingsFilterChange?.('');
      onLocationFilterChange?.('');
    }
    onResetCustomFilters?.();
  }, [
    table,
    onClubFilterChange,
    onPlanFilterChange,
    onPaymentMethodFilterChange,
    onStatusFilterChange,
    onRatingsFilterChange,
    onLocationFilterChange,
    onResetCustomFilters,
  ]);

  const { t } = useDynamicLocale();
  const hideSearch = table.options.meta?.hideSearch ?? false;
  const isLocationLabel = (label: string) =>
    label === t('common.page.tableColumn.location') || label === t('common.page.tableColumn.prefecture');

  function ColumnDropdownFilter<T>({ table }: { table: Table<T> }) {
    const columns = table.getAllColumns().filter((col) => col.columnDef.meta?.options?.length);
    const columnSelectClassName = columns.find((col) => col.columnDef.meta?.statusClassName)?.columnDef.meta
      ?.statusClassName;

    if (!columns.length && !(hasViewOptions && hasDateFilter && createdDateColumn) && !filterChildren) return null;

    return (
      <div
        className={cn(
          'flex w-full flex-wrap items-center gap-5 justify-end px-4 py-3',
          columnSelectClassName,
          selectClassName,
        )}
      >
        {filterChildren}
        {columns.map((column) => {
          const options = column.columnDef.meta?.options ?? [];
          const label = column.columnDef.meta?.label ?? '';
          const statusValue = (statusFilter as string) ?? '';
          const clubValue = (clubFilter as string) ?? '';
          const planValue = (planFilter as string) ?? '';
          const paymentMethodValue = (paymentMethodFilter as string) ?? '';
          const ratingValue = (ratingsFilter as string) ?? '';
          const locationValue = (locationFilter as string) ?? '';

          return (
            <Select
              key={column.id}
              label={label}
              className={label === 'Plan Type' ? 'min-w-[155px]' : ''}
              placeholder={t('common.form.placeholder.select')}
              options={options}
              value={
                label === t('common.page.tableColumn.status')
                  ? statusValue
                  : label === 'Club Name'
                    ? clubValue
                    : label === t('dashboard.reservationLedger.table.filter.planType')
                      ? planValue
                      : label === t('common.page.tableColumn.paymentMethod')
                        ? paymentMethodValue
                        : label === t('faq.reviewRating.table.ratings')
                          ? ratingValue
                          : isLocationLabel(label)
                            ? locationValue
                            : ''
              }
              onValueChange={(val: string) => {
                if (label === t('common.page.tableColumn.status') && onStatusFilterChange) {
                  onStatusFilterChange(val);
                } else if (label === 'Club Name' && onClubFilterChange) {
                  onClubFilterChange(val);
                } else if (label === t('dashboard.reservationLedger.table.filter.planType') && onPlanFilterChange) {
                  onPlanFilterChange(val);
                } else if (label === t('common.page.tableColumn.paymentMethod') && onPaymentMethodFilterChange) {
                  onPaymentMethodFilterChange(val);
                } else if (label === t('faq.reviewRating.table.ratings') && onRatingsFilterChange) {
                  onRatingsFilterChange(val);
                } else if (isLocationLabel(label) && onLocationFilterChange) {
                  onLocationFilterChange(val);
                }
              }}
            />
          );
        })}

        {hasViewOptions && hasDateFilter && createdDateColumn && (
          <div>
            <DataTableDateFilter
              column={createdDateColumn}
              title={t('common.page.search.date')}
              multiple
              allowFutureDates={allowFutureDates}
              defaultPreset={activeDatePreset}
              defaultPresetIsFilter={defaultDatePresetIsFilter}
              showDefaultRangeWhenEmpty={showDefaultDateRangeWhenEmpty}
            />
          </div>
        )}
      </div>
    );
  }

  const renderActionButtons = () => {
    if (table.options.meta?.actions?.length) {
      return table.options.meta.actions.map((action) => (
        <Button
          key={action.key}
          onClick={action.onClick}
          disabled={action.disabled}
          style={{ cursor: action.disabled ? 'not-allowed' : 'pointer' }}
          className={cn(
            'flex items-center justify-center gap-2',
            action.key === 'exportCsv'
              ? 'text-primary bg-card border-primary border hover:bg-card disabled:bg-surface-track disabled:border-border-subtle disabled:text-foreground-soft'
              : '',
            action.key === 'onBlock'
              ? ' text-contrast p-2 pl-2.5 pr-2.25 h-9 bg-card border border-primary shadow-none hover:bg-surface-hover-strong'
              : '',
          )}
        >
          {action.icon}
          {action.label ? (
            action.label
          ) : action.key === 'onAddNew' ? (
            <Translation id="userManagement.rolesAndPrivileges.table.addNew" />
          ) : action.key === 'exportCsv' ? (
            <Translation id="common.page.table.exportCSV" />
          ) : null}
        </Button>
      ));
    }

    if (actionKey && action) {
      return (
        <Button
          onClick={action}
          disabled={actionDisabled}
          style={{ cursor: actionDisabled ? 'not-allowed' : 'pointer' }}
          className="flex items-center justify-center gap-2"
        >
          {actionKey === 'onAddNew' ? <PlusIcon /> : <CsvIcon />}
          {buttonText ? (
            <Translation id={buttonText} />
          ) : actionKey === 'onAddNew' ? (
            <Translation id="userManagement.rolesAndPrivileges.table.addNew" />
          ) : (
            <Translation id="common.page.table.exportCSV" />
          )}
        </Button>
      );
    }

    return null;
  };

  const filterableColumns = columns.filter((column) => column.id !== 'createdDate' || !hasViewOptions);
  const shouldShowToolbarFilters = filterableColumns.length > 0 || isFiltered;

  return (
    <>
      {(!hideSearch || title) && (
        <div className="pt-3 px-4 flex w-full items-center gap-4">
          <Label className="font-bold text-md flex flex-shrink-0 self-start xl:self-center">{title}</Label>

          <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
            {children}

            {dateFilterFirst && hasDateFilter && createdDateColumn && (
              <DataTableDateFilter
                column={createdDateColumn}
                title={t('common.page.search.date')}
                multiple
                allowFutureDates={allowFutureDates}
                defaultPreset={activeDatePreset}
                defaultPresetIsFilter={defaultDatePresetIsFilter}
                showDefaultRangeWhenEmpty={showDefaultDateRangeWhenEmpty}
              />
            )}

            {!hideSearch && (
              <div className="flex items-center rounded border border-surface-hover px-2">
                <SearchIcon className="text-secondary stroke-1.5" size={20} />
                <Input
                  placeholder={table.options.meta?.searchPlaceholder ?? t('common.page.search')}
                  value={(table.getState().globalFilter as string) ?? ''}
                  onChange={(e) => {
                    table.setGlobalFilter(sanitizeTableSearch(e.target.value));
                  }}
                  className="h-8 w-40 lg:w-56 border-0 outline-none focus-visible:ring-0 shadow-none"
                />
              </div>
            )}

            {!dateFilterFirst && !hasViewOptions && hasDateFilter && createdDateColumn && (
              <DataTableDateFilter
                column={createdDateColumn}
                title={t('common.page.search.date')}
                multiple
                allowFutureDates={allowFutureDates}
                defaultPreset={activeDatePreset}
                defaultPresetIsFilter={defaultDatePresetIsFilter}
                showDefaultRangeWhenEmpty={showDefaultDateRangeWhenEmpty}
              />
            )}

            {hasViewOptions && <DataTableViewOptions table={table} />}

            <div className="flex items-center gap-2">
              {renderActionButtons()}

              {shouldShowToolbarFilters && (
                <div role="toolbar" aria-orientation="horizontal" className={cn(className)} {...props}>
                  <div>
                    {columns
                      .filter((column) => column.id !== 'createdDate' || !hasViewOptions)
                      .map((column) => (
                        <DataTableToolbarFilter key={column.id} column={column} />
                      ))}

                    {isFiltered && (
                      <Button aria-label="Reset filters" variant="filter" className="size-9" onClick={onReset}>
                        <X />
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div>
        <ColumnDropdownFilter table={table} />
        <DataTablePagination table={table} />
      </div>
    </>
  );
}

interface DataTableToolbarFilterProps<TData> {
  column: Column<TData>;
}

function DataTableToolbarFilter<TData>({ column }: DataTableToolbarFilterProps<TData>) {
  const columnMeta = column.columnDef.meta;

  const onFilterRender = React.useCallback(() => {
    if (!columnMeta?.variant) return null;

    switch (columnMeta.variant) {
      case 'number':
        return (
          <div className="relative">
            <Input
              type="number"
              inputMode="numeric"
              placeholder={columnMeta.placeholder ?? columnMeta.label}
              value={(column.getFilterValue() as string) ?? ''}
              onChange={(event) => column.setFilterValue(event.target.value)}
              className={cn('h-8 w-[120px]', columnMeta.unit && 'pr-8')}
            />
            {columnMeta.unit && (
              <span className="absolute top-0 right-0 bottom-0 flex items-center rounded-r-md bg-accent px-2 text-muted-foreground text-sm">
                {columnMeta.unit}
              </span>
            )}
          </div>
        );

      case 'range':
        return <DataTableSliderFilter column={column} title={columnMeta.label ?? column.id} />;

      case 'select':
      case 'multiSelect':
        return (
          <DataTableFacetedFilter
            column={column}
            title={columnMeta.label ?? column.id}
            options={columnMeta.options ?? []}
            multiple={columnMeta.variant === 'multiSelect'}
          />
        );

      default:
        return null;
    }
  }, [column, columnMeta]);

  return onFilterRender();
}
